import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { RotateCcw, Copy, ArrowRightLeft, Shield, BookOpen } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

const ROT13Converter: React.FC = () => {
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');

  useSEO({
    title: 'ROT13 Converter - Encode & Decode ROT13 Text Online',
    description: 'Convert text using ROT13 cipher. Simple letter substitution cipher that replaces each letter with the letter 13 positions after it. Free online tool.',
    keywords: 'ROT13 converter, ROT13 encoder, ROT13 decoder, cipher tool, text encryption, letter substitution',
    canonical: 'https://snap-tools.online/security/rot13-converter'
  });

  const rot13Transform = (text: string): string => {
    return text.replace(/[A-Za-z]/g, (char) => {
      const start = char <= 'Z' ? 65 : 97; // ASCII value for 'A' or 'a'
      const code = char.charCodeAt(0);
      const shifted = ((code - start + 13) % 26) + start;
      return String.fromCharCode(shifted);
    });
  };

  const handleConvert = () => {
    if (!inputText.trim()) {
      toast.error('Please enter text to convert');
      return;
    }

    const result = rot13Transform(inputText);
    setOutputText(result);
    toast.success('Text converted successfully');
  };

  const swap = () => {
    setInputText(outputText);
    setOutputText(inputText);
  };

  const copyToClipboard = async (text: string, type: string) => {
    if (!text) {
      toast.error(`No ${type} to copy`);
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${type} copied to clipboard`);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const reset = () => {
    setInputText('');
    setOutputText('');
  };

  // Example texts for demonstration
  const examples = [
    { original: 'Hello World!', encoded: 'Uryyb Jbeyq!' },
    { original: 'Secret Message', encoded: 'Frperg Zrffntr' },
    { original: 'ROT13 is fun!', encoded: 'EBG13 vf sha!' },
    { original: 'Programming', encoded: 'Cebtenzzvat' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
              <Shield className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">ROT13 Converter</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Encode and decode text using the ROT13 cipher. ROT13 is a simple letter substitution cipher that replaces each letter with the letter 13 positions after it in the alphabet.
            </p>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          {/* Input Section */}
          <div className="space-y-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter text to convert:
              </label>
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Type your message here..."
                className="w-full h-32 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                ROT13 is its own inverse - encoding and decoding use the same operation.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 justify-center">
              <button
                onClick={handleConvert}
                disabled={!inputText.trim()}
                className="bg-purple-600 text-white py-3 px-8 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Convert ROT13
              </button>
              
              <button
                onClick={swap}
                disabled={!outputText}
                className="flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Swap input and output"
              >
                <ArrowRightLeft className="w-4 h-4" />
                Swap
              </button>
              
              <button
                onClick={reset}
                className="flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Reset
              </button>
            </div>
          </div>

          {/* Output Section */}
          {outputText && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-50 rounded-lg p-6 mb-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Converted Text:</h3>
                <button
                  onClick={() => copyToClipboard(outputText, 'converted text')}
                  className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  Copy
                </button>
              </div>
              
              <div className="bg-white rounded border p-4">
                {outputText}
              </div>
            </motion.div>
          )}

          {/* Examples Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="w-5 h-5 text-blue-600" />
              <h4 className="font-semibold text-blue-900">Examples</h4>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {examples.map((example, index) => (
                <div key={index} className="bg-white rounded p-3">
                  <div className="text-sm">
                    <div className="font-medium text-gray-900 mb-1">Original:</div>
                    <div className="text-gray-700 mb-2">{example.original}</div>
                    <div className="font-medium text-blue-900 mb-1">ROT13:</div>
                    <div className="text-blue-700 font-mono">{example.encoded}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mb-6"
          />

          {/* ROT13 Reference */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
            <h4 className="font-semibold text-green-900 mb-4">ROT13 Alphabet Reference</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h5 className="font-medium text-green-800 mb-2">A-M → N-Z</h5>
                <div className="grid grid-cols-6 gap-1 text-sm font-mono">
                  {['A→N', 'B→O', 'C→P', 'D→Q', 'E→R', 'F→S', 'G→T', 'H→U', 'I→V', 'J→W', 'K→X', 'L→Y', 'M→Z'].map((mapping, index) => (
                    <div key={index} className="bg-white rounded px-2 py-1 text-center text-green-700">
                      {mapping}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h5 className="font-medium text-green-800 mb-2">N-Z → A-M</h5>
                <div className="grid grid-cols-6 gap-1 text-sm font-mono">
                  {['N→A', 'O→B', 'P→C', 'Q→D', 'R→E', 'S→F', 'T→G', 'U→H', 'V→I', 'W→J', 'X→K', 'Y→L', 'Z→M'].map((mapping, index) => (
                    <div key={index} className="bg-white rounded px-2 py-1 text-center text-green-700">
                      {mapping}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Educational Content */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-yellow-50 rounded-lg p-6">
              <h4 className="font-semibold text-yellow-900 mb-3">About ROT13</h4>
              <ul className="text-sm text-yellow-800 space-y-2">
                <li>• <strong>Self-Inverse:</strong> Applying ROT13 twice returns the original text</li>
                <li>• <strong>Letters Only:</strong> Only alphabetic characters are transformed</li>
                <li>• <strong>Case Preserved:</strong> Uppercase and lowercase are maintained</li>
                <li>• <strong>Not Secure:</strong> ROT13 is not a secure encryption method</li>
                <li>• <strong>Obfuscation:</strong> Used to hide spoilers, answers, or offensive content</li>
                <li>• <strong>Usenet Origin:</strong> Popularized in early internet forums</li>
              </ul>
            </div>
            
            <div className="bg-red-50 rounded-lg p-6">
              <h4 className="font-semibold text-red-900 mb-3">Security Notice</h4>
              <ul className="text-sm text-red-800 space-y-2">
                <li>• <strong>Not Encryption:</strong> ROT13 is easily broken and provides no security</li>
                <li>• <strong>Obfuscation Only:</strong> Use only to hide text temporarily</li>
                <li>• <strong>No Passwords:</strong> Never use ROT13 for sensitive information</li>
                <li>• <strong>Educational Purpose:</strong> Good for learning basic cipher concepts</li>
                <li>• <strong>Public Knowledge:</strong> Anyone can decode ROT13 instantly</li>
                <li>• <strong>Use Real Encryption:</strong> For security, use proper encryption methods</li>
              </ul>
            </div>
          </div>

          {/* How It Works */}
          <div className="mt-6 bg-gray-50 rounded-lg p-6">
            <h4 className="font-semibold text-gray-900 mb-3">How ROT13 Works</h4>
            <div className="text-sm text-gray-700 space-y-2">
              <p>
                <strong>Formula:</strong> For each letter, replace it with the letter 13 positions later in the alphabet.
              </p>
              <p>
                <strong>Example:</strong> 'A' (position 1) becomes 'N' (position 14), 'B' becomes 'O', etc.
              </p>
              <p>
                <strong>Wraparound:</strong> After 'M', it wraps around: 'N' becomes 'A', 'O' becomes 'B', etc.
              </p>
              <p>
                <strong>Non-letters:</strong> Numbers, punctuation, and spaces remain unchanged.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ROT13Converter;
